import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, Optional } from '@angular/core';
import { environment } from '../../environments/environment';
import { HOST_PROJECT_NAME, ProjectName, ProjectNames } from '../tokens';
import { LocalStorageKey, LocalStorageService } from './local-storage.service';
import { UserInfoService } from './user-info.service';

/** NewsBreak's own enum: 0 female, 1 male, 2 unknown/non-binary. */
export type NewsbreakGender = 0 | 1 | 2;

/** NewsBreak's own buckets: 1 = 18-24, 2 = 25-34, 3 = 35-44, 4 = 45-54, 5 = 55-64, 6 = 65+. */
export type NewsbreakAgeRange = 1 | 2 | 3 | 4 | 5 | 6;

interface NewsbreakDemographics {
  nb_ge?: NewsbreakGender;
  nb_ar?: NewsbreakAgeRange;
}

/**
 * Service for NewsBreak pixel tracking
 * Documentation: https://adhelp.newsbreak.com/hc/en-us/articles/46523942907277-Best-Practices-for-Event-Tracking
 */
@Injectable({
  providedIn: 'root',
})
export class NewsbreakService {
  // Real id only in prod; non-prod builds get a deliberately wrong id from the environment
  private readonly pixelId = environment.newsbreakPixelId;
  private readonly scriptSelector = 'script[data-newsbreak-pixel="true"]';
  private isInitialized = false;
  private readonly firedOnce = new Set<string>();

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private userInfo: UserInfoService,
    private storage: LocalStorageService,
    @Optional() @Inject(HOST_PROJECT_NAME) private hostProjectName?: ProjectName
  ) {}

  init(): void {
    if (this.isInitialized || !this.isEnabledProject() || typeof window === 'undefined') {
      return;
    }

    this.isInitialized = true;

    const targetWindow = window as Window &
      typeof globalThis & {
        nbpix?: NewsbreakTracker;
      };

    if (!targetWindow.nbpix) {
      const newsbreakQueue = function (...args: unknown[]) {
        if (newsbreakQueue.process) {
          newsbreakQueue.process.apply(newsbreakQueue, args);
        } else {
          newsbreakQueue.queue.push(args);
        }
      } as NewsbreakTracker;

      newsbreakQueue.queue = [];
      newsbreakQueue.t = Date.now();
      targetWindow.nbpix = newsbreakQueue;

      if (!this.document.querySelector(this.scriptSelector)) {
        const script = this.document.createElement('script');
        script.async = true;
        script.dataset['newsbreakPixel'] = 'true';
        script.src = this.getScriptUrl();

        const firstScript = this.document.getElementsByTagName('script')[0];
        if (firstScript?.parentNode) {
          firstScript.parentNode.insertBefore(script, firstScript);
        } else {
          this.document.head.appendChild(script);
        }
      }
    }

    targetWindow.nbpix('init', this.pixelId);
    targetWindow.nbpix('event', 'pageload');
  }

  /**
   * Demographics for NewsBreak's advanced matching, already mapped to NewsBreak's enums by the
   * caller. Persisted because Stripe's redirect back to `/payment-result` reloads the app, and
   * `complete_payment` then fires in a fresh JS context with nothing left of the quiz answers.
   */
  identify(demographics: {
    gender?: NewsbreakGender | null;
    ageRange?: NewsbreakAgeRange | null;
  }): void {
    if (!this.isEnabledProject()) {
      return;
    }

    const stored = this.storedDemographics();

    // `null` clears: a visitor who returns to the step and declines must stop matching on the
    // answer they gave the first time round. `undefined` leaves the field as it is.
    if (demographics.gender !== undefined) {
      stored.nb_ge = demographics.gender ?? undefined;
    }

    if (demographics.ageRange !== undefined) {
      stored.nb_ar = demographics.ageRange ?? undefined;
    }

    this.storage.set(LocalStorageKey.newsbreakDemographics, stored);
  }

  /**
   * The `view_content` step of NewsBreak's recommended funnel: the visitor engaged past the
   * first screen. Mirrors the `web_first_screen_passed` Amplitude event.
   */
  viewContent(): void {
    this.trackOnce('view_content');
  }

  completeRegistration(email: string): void {
    this.track('complete_registration', undefined, email);
  }

  addToCart(): void {
    this.trackOnce('add_to_cart');
  }

  beginCheckout(): void {
    this.track('initiate_checkout');
  }

  completePayment(payment: { value?: number; currency?: string }): void {
    // No value rather than a guessed one: the server-side postback carries the authoritative
    // revenue, and a placeholder amount would train NewsBreak's optimizer on the wrong number.
    this.track('complete_payment', {
      ...(payment.value === undefined ? {} : { nb_value: payment.value }),
      ...(payment.currency ? { nb_currency: payment.currency } : {}),
    });
  }

  // Paywall and first-quiz-step components re-run on back navigation, and NewsBreak counts every
  // repeat as a separate conversion.
  private trackOnce(eventName: string, eventData?: Record<string, unknown>): void {
    if (this.firedOnce.has(eventName)) {
      return;
    }

    this.firedOnce.add(eventName);
    this.track(eventName, eventData);
  }

  private track(eventName: string, eventData?: Record<string, unknown>, email?: string): void {
    void this.withUserParams(eventData, email).then(data => this.send(eventName, data));
  }

  /**
   * Identity NewsBreak matches conversions on. `ext_id` is the Stripe customer id, the same key
   * the server-side postback reports under; `nb_em` must be SHA-256 of the trimmed, lowercased
   * address.
   */
  private async withUserParams(
    eventData?: Record<string, unknown>,
    email?: string
  ): Promise<Record<string, unknown>> {
    const data: Record<string, unknown> = { ...this.storedDemographics(), ...eventData };
    const externalId = this.userInfo.getStripeCustomerId();

    if (externalId) {
      data['ext_id'] = externalId;
    }

    const normalizedEmail = this.normalizeEmail(email ?? this.userInfo.getEmail() ?? '');

    if (normalizedEmail) {
      try {
        data['nb_em'] = await this.hashSha256(normalizedEmail);
      } catch (error) {
        console.error('NewsBreak email hashing error:', error);
      }
    }

    return data;
  }

  private send(eventName: string, eventData: Record<string, unknown>): void {
    try {
      if (typeof nbpix === 'function') {
        nbpix('event', eventName, eventData);
      }
    } catch (error) {
      console.error('NewsBreak tracking error:', error);
    }
  }

  private storedDemographics(): NewsbreakDemographics {
    return this.storage.get<NewsbreakDemographics>(LocalStorageKey.newsbreakDemographics) ?? {};
  }

  private normalizeEmail(email: string): string {
    return email.trim().toLowerCase();
  }

  private async hashSha256(value: string): Promise<string> {
    const cryptoApi = globalThis.crypto;

    if (!cryptoApi?.subtle) {
      throw new Error('Web Crypto API is not available');
    }

    const encodedValue = new TextEncoder().encode(value);
    const hashBuffer = await cryptoApi.subtle.digest('SHA-256', encodedValue);

    return Array.from(new Uint8Array(hashBuffer))
      .map(byte => byte.toString(16).padStart(2, '0'))
      .join('');
  }

  private getScriptUrl(): string {
    const nextMidnightEpochDays = Math.ceil(Date.now() / 864e5);

    return `https://static.newsbreak.com/business/tracking/nbpixel.js?t=${864e5 * nextMidnightEpochDays}`;
  }

  // Scoped by deployment, not by flow: the AppLovin build swaps PROJECT_NAME to the main or
  // Kindle funnel via `?funnel=`, which would otherwise fire NewsBreak on AppLovin traffic.
  private isEnabledProject(): boolean {
    const projectName = this.hostProjectName ?? ProjectNames.FUNNEL;

    return (
      projectName === ProjectNames.FUNNEL ||
      projectName === ProjectNames.GOOGLE_FUNNEL ||
      projectName === ProjectNames.KINDLE_FUNNEL ||
      projectName === ProjectNames.ROMANCE_FUNNEL
    );
  }
}

interface NewsbreakTracker {
  (...args: unknown[]): void;
  process?: (...args: unknown[]) => void;
  queue: unknown[][];
  t: number;
}
